import { Table as MantineTable } from '@mantine/core'
import { useCallback } from 'react'
import { useRecoilState } from 'recoil'

import { TableAtom } from '../recoil/atoms'
import { generateUUID, parseNumber } from '../utils/display'
import DataNotFound from './DataNotFound'
import SortArrow from './SortArrow'

interface RankingTableProps {
  name: string
  labels: string[]
  unSortableIndex?: number[]
  rightAlignedColumns?: number[]
  elements: (string | React.ReactNode)[][]
  current: number
  pageSize: number
  columnWidths: string[]
}

function RankingTable({ name, labels, unSortableIndex = [], rightAlignedColumns = [], elements, current, pageSize, columnWidths }: RankingTableProps) {
  const [tableOpt, setTableOpt] = useRecoilState(TableAtom(name))
  const start = (current - 1) * pageSize

  const handleSort = useCallback(
    (index: number) => {
      if (tableOpt.sortTarget === index) {
        setTableOpt((prev) => ({ ...prev, isAsc: !prev.isAsc }))
      } else {
        setTableOpt((prev) => ({ ...prev, sortTarget: index, isAsc: false }))
      }
    },
    [setTableOpt, tableOpt.sortTarget],
  )

  const applySort = useCallback(
    (index: number) => {
      if (index === tableOpt.sortTarget) {
        return tableOpt.isAsc ? 1 : -1
      }
      return 0
    },
    [tableOpt.sortTarget, tableOpt.isAsc],
  )

  if (elements.length === 0) {
    return <DataNotFound className='h-[45vh]' />
  }

  return (
    <div className='h-[45vh] overflow-y-auto'>
      <MantineTable striped highlightOnHover stickyHeader>
        <MantineTable.Thead>
          <MantineTable.Tr>
            <MantineTable.Th key={generateUUID()}>順位</MantineTable.Th>
            {labels.map((label, index) =>
              unSortableIndex.includes(index) ? (
                <MantineTable.Th key={generateUUID()} style={{ width: columnWidths[index] || 'auto' }}>
                  <div className='flex items-center'>
                    {label}
                    <SortArrow status={9} />
                  </div>
                </MantineTable.Th>
              ) : (
                <MantineTable.Th className='hover:opacity-70' key={generateUUID()} style={{ width: columnWidths[index] || 'auto' }}>
                  <button type='button' className='flex items-center' onClick={() => handleSort(index)}>
                    {label}
                    <SortArrow status={applySort(index)} />
                  </button>
                </MantineTable.Th>
              ),
            )}
          </MantineTable.Tr>
        </MantineTable.Thead>
        <MantineTable.Tbody>
          {elements.map((tbodys, index) => (
            <MantineTable.Tr key={generateUUID()}>
              <MantineTable.Td key={generateUUID()}>{start + index + 1}</MantineTable.Td>
              {tbodys.map((value, colIndex) => (
                <MantineTable.Td 
                  key={generateUUID()} 
                  className={rightAlignedColumns.includes(colIndex) ? 'text-right' : ''}
                >
                  {typeof value === 'number' ? parseNumber(value) : value}
                </MantineTable.Td>
              ))}
            </MantineTable.Tr>
          ))}
        </MantineTable.Tbody>
      </MantineTable>
    </div>
  )
}

RankingTable.defaultProps = {
  unSortableIndex: [],
  rightAlignedColumns: [],
}

export default RankingTable